import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/api/client';
import ConfirmChoiceCard from './ConfirmChoiceCard';
import ConfirmDestructiveDialog from './ConfirmDestructiveDialog';
import { InlineToggle } from '@/components/ui';
import type { DeleteImpact, Recipient } from '@/types';
import {
  recipientPrimaryLabel,
  recipientSecondaryLabel,
} from '@/utils/recipientDisplay';

type Action = 'disable' | 'delete';

type Props = {
  recipient: Recipient;
  onClose: () => void;
  onDone: (message: string) => void;
};

export default function ConfirmRecipientDeleteModal({ recipient, onClose, onDone }: Props) {
  const [impact, setImpact] = useState<DeleteImpact | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [action, setAction] = useState<Action>('disable');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const primaryLabel = useMemo(() => recipientPrimaryLabel(recipient), [recipient]);
  const secondaryLabel = useMemo(() => recipientSecondaryLabel(recipient), [recipient]);

  useEffect(() => {
    setLoading(true);
    setError('');
    setAction('disable');
    setConfirmDelete(false);
    api.deleteImpact
      .recipient(recipient.id)
      .then(setImpact)
      .catch((e) => setError(e instanceof Error ? e.message : 'Load failed'))
      .finally(() => setLoading(false));
  }, [recipient.id]);

  const subscriptionCount = Number(impact?.will_remove.subscriptions ?? recipient.subscription_count ?? 0);
  const deliveryCount =
    typeof impact?.will_keep.delivery_records === 'number' ? impact.will_keep.delivery_records : 0;

  async function confirm() {
    setSubmitting(true);
    setError('');
    try {
      const result =
        action === 'disable'
          ? await api.recipients.disable(recipient.id)
          : await api.recipients.delete(recipient.id, { remove_subscriptions: true });

      const parts = [...result.removed, ...result.kept];
      const message =
        action === 'disable'
          ? parts.join('. ') || `Recipient ${primaryLabel} disabled`
          : parts.join('. ') || `Recipient ${primaryLabel} deleted`;

      onDone(message);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setSubmitting(false);
    }
  }

  const confirmDisabled =
    submitting ||
    loading ||
    !impact ||
    (action === 'delete' && !confirmDelete);

  const confirmLabel =
    action === 'disable'
      ? submitting
        ? 'Disabling…'
        : 'Disable recipient'
      : submitting
        ? 'Deleting…'
        : 'Delete recipient';

  return (
    <ConfirmDestructiveDialog
      title="Remove recipient"
      onClose={onClose}
      closeOnBackdrop={!submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className={action === 'delete' ? 'btn btn-danger' : 'btn btn-primary'}
            disabled={confirmDisabled}
            onClick={confirm}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {loading && <p className="muted">Loading…</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="recipient-delete-identity">
        <p className="modal-lead">
          <strong>{primaryLabel}</strong>
        </p>
        {secondaryLabel && <p className="muted recipient-delete-secondary">{secondaryLabel}</p>}
        <p className="muted modal-hint recipient-delete-stats">
          {subscriptionCount} subscription{subscriptionCount === 1 ? '' : 's'} · {deliveryCount} deliver
          {deliveryCount === 1 ? 'y' : 'ies'}
        </p>
      </div>

      {impact && (
        <>
          <fieldset className="confirm-choice-group">
            <legend className="confirm-choice-group__legend">Action</legend>
            <ConfirmChoiceCard
              compact
              name="recipient-action"
              value="disable"
              checked={action === 'disable'}
              onSelect={() => setAction('disable')}
              variant="safe"
              title="Disable"
              description="Stops new notifications. Keeps history."
            />
            <ConfirmChoiceCard
              compact
              name="recipient-action"
              value="delete"
              checked={action === 'delete'}
              onSelect={() => setAction('delete')}
              variant="destructive"
              title="Delete"
              description="Deletes recipient and subscriptions. Keeps delivery history."
            />
          </fieldset>

          {action === 'delete' && (
            <div className="confirm-destructive-confirm">
              {impact.subscription_names && impact.subscription_names.length > 0 && (
                <p className="option-note muted">
                  Subscriptions to remove: {impact.subscription_names.join(', ')}
                </p>
              )}
              <InlineToggle
                checked={confirmDelete}
                onChange={setConfirmDelete}
                label="I understand this is permanent"
              />
            </div>
          )}
        </>
      )}
    </ConfirmDestructiveDialog>
  );
}
